import { EventType, EventRepeatRule } from "@/api/clubsApi";
import type { ImageUploadResult } from "@/utils/uploadUtils";

/** The shape of the mutation argument for `createEvent` or `updateEvent`. */
export interface CreateEventParams {
  id?: string; // Present in edit mode for updateEvent
  club_id: string;
  creator_id: string;
  title: string;
  event_type: EventType;
  description?: string;
  location_name?: string;
  location_object?: unknown;
  start_time?: string;
  end_time?: string;
  timezone?: string;
  is_all_day?: boolean;
  is_virtual?: boolean;
  virtual_link_url?: string;
  allows_public_link?: boolean;
  allows_quick_replies?: boolean;
  primary_image_small?: string;
  primary_image_medium?: string;
  primary_image_full?: string;
  repeat_rule?: EventRepeatRule | null;
}

/** All form-state fields needed to build the mutation payload. */
export interface CreateEventFormState {
  clubId: string;
  creatorId: string;
  title: string;
  description: string;
  isHang: boolean;
  locationName: string;
  locationObject: unknown | null;
  startTimeISO: string | null;
  endTimeISO: string | null;
  userTimezone: string;
  // Hang-specific
  allowsQuickReplies: boolean;
  // Gather-specific
  isAllDay: boolean;
  isVirtual: boolean;
  virtualLinkUrl: string;
  shareableLink: boolean;
  // Edit mode — when set, the returned params include `id` for updateEvent
  eventId?: string;
  // Image upload result from R2
  imageUploadResult?: ImageUploadResult | null;
  // Repeat rule (events only, not hangs)
  repeatRule?: EventRepeatRule | null;
}

/**
 * Pure function that converts UI form state into the mutation payload
 * for `createEvent`. Extracted from `handleSubmit` for testability.
 */
export function buildCreateEventParams(state: CreateEventFormState): CreateEventParams {
  const params: CreateEventParams = {
    club_id: state.clubId,
    creator_id: state.creatorId,
    title: state.title.trim() || (state.isHang ? "Hang" : "Untitled Event"),
    event_type: state.isHang ? EventType.hang : EventType.event,
    timezone: state.userTimezone,
  };

  if (state.eventId) {
    params.id = state.eventId;
  }

  if (state.description.trim()) {
    params.description = state.description.trim();
  }

  // Location name: prefer custom name, fall back to place name from feature
  if (state.locationName.trim()) {
    params.location_name = state.locationName.trim();
  } else if (state.locationObject && typeof state.locationObject === "object") {
    const obj = state.locationObject as { properties?: { name_preferred?: string; name?: string } };
    if (obj.properties) {
      params.location_name = obj.properties.name_preferred || obj.properties.name;
    }
  }

  if (state.locationObject) {
    params.location_object = state.locationObject;
  }

  if (state.startTimeISO) params.start_time = state.startTimeISO;
  if (state.endTimeISO) params.end_time = state.endTimeISO;

  if (state.isHang) {
    params.allows_quick_replies = state.allowsQuickReplies;
  } else {
    params.is_all_day = state.isAllDay;
    params.is_virtual = state.isVirtual;
    if (state.isVirtual && state.virtualLinkUrl.trim()) {
      params.virtual_link_url = state.virtualLinkUrl.trim();
    }
    params.allows_public_link = state.shareableLink;
  }

  if (state.imageUploadResult) {
    params.primary_image_small = state.imageUploadResult.imageSmall;
    params.primary_image_medium = state.imageUploadResult.imageMedium;
    params.primary_image_full = state.imageUploadResult.imageFull;
  }

  // Repeat rule — events only, not hangs (matches mobile: isHangMode ? null : repeatRule)
  if (!state.isHang) {
    params.repeat_rule = state.repeatRule ?? null;
  }

  return params;
}
